import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  Home, 
  Compass, 
  User, 
  Settings, 
  CreditCard,
  LogOut,
  Menu,
  X,
  Zap,
  DollarSign,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/explore", icon: Compass, label: "Explore" },
    { path: "/profile", icon: User, label: "Saved", protected: true },
    { path: "/analytics", icon: BarChart3, label: "Analytics", protected: true },
    { path: "/affiliate", icon: DollarSign, label: "Earn", protected: true, premium: true },
    { path: "/settings", icon: Settings, label: "Settings", protected: true },
    { path: "/payment", icon: CreditCard, label: "Premium", protected: true },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 md:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-md">
              <span className="text-white font-black text-lg">TE</span>
            </div>
            <span className="font-['Syne'] text-xl font-extrabold tracking-tight hidden sm:block bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              TrendEngine
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              if (item.protected && !user) return null;
              if (item.premium && !user?.is_premium) return null;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-2 font-medium transition-all ${
                    isActive(item.path)
                      ? "bg-black text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Auth Buttons / User Menu */}
          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden md:flex items-center gap-3">
                <div className="flex items-center gap-2">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 border-2 border-black object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-[#CCFF00] border-2 border-black flex items-center justify-center font-bold">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium text-sm">{user.name}</span>
                  {user.is_premium && (
                    <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs px-2.5 py-0.5 font-bold uppercase tracking-wide shadow-sm">
                      PRO
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link to="/auth" data-testid="login-btn">
                <Button className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold">
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t-2 border-black bg-white">
            <nav className="flex flex-col p-4 gap-1">
              {navItems.map((item) => {
                if (item.protected && !user) return null;
                if (item.premium && !user?.is_premium) return null;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 font-medium transition-all ${
                      isActive(item.path)
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              {user && (
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-logout-btn"
                  className="flex items-center gap-3 px-4 py-3 font-medium text-red-600 hover:bg-red-50 mt-2 border-t border-gray-200"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-73px)]">
        <Outlet />
      </main>

      {/* Footer with FTC Disclosure */}
      <footer className="border-t-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Brand */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <span className="text-white font-black text-sm">TE</span>
            </div>
            <span className="font-bold text-lg bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              TrendEngine
            </span>
          </div>
          
          {/* FTC Disclosure - 2026 Compliance */}
          <div className="text-center mb-6 max-w-3xl mx-auto">
            <p className="text-xs text-gray-600 leading-relaxed">
              <span className="font-semibold">FTC Disclosure:</span> As a TrendEngine partner, we may earn from qualifying purchases made through marketplace links. 
              This helps us provide free trend analysis to all users. Prices and availability are subject to change.
            </p>
          </div>
          
          {/* Footer Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 mb-4">
            <span className="font-semibold text-gray-700">© 2026 TrendEngine</span>
            <span className="text-gray-300">•</span>
            <a href="/terms" className="hover:text-purple-600 transition-colors font-medium">Terms of Service</a>
            <span className="text-gray-300">•</span>
            <a href="/privacy" className="hover:text-purple-600 transition-colors font-medium">Privacy Policy</a>
            <span className="text-gray-300">•</span>
            <a href="/contact" className="hover:text-purple-600 transition-colors font-medium">Contact</a>
          </div>
          
          {/* Made with Emergent */}
          <div className="text-center">
            <span className="text-[10px] text-gray-400">Powered by Emergent AI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
